import React, { useState, useEffect } from 'react';
import {
  Phone,
  Mail,
  Clock,
  ChevronDown,
  Menu,
  X,
  ExternalLink,
  Layers,
  Cpu,
  TrendingUp,
  Server,
  Globe,
  ArrowRight,
  ShoppingBag,
  Sparkles,
  CreditCard,
  Building2,
  Code2,
  Smartphone,
  ShieldCheck,
  Bot,
  Search,
  Lock,
  MessageSquare,
  Home,
  FileSpreadsheet,
  Zap,
  CheckCircle2
} from 'lucide-react';
import { SITE_CONTACT } from '../data/siteData';
import { trackCtaClick } from '../utils/analytics';

interface HeaderProps {
  currentView: string;
  onNavigate: (view: string, targetId?: string) => void;
  onOpenBooking: () => void;
  onOpenPortal: () => void;
  onOpenPaybill?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  currentView,
  onNavigate,
  onOpenBooking,
  onOpenPortal,
  onOpenPaybill,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [appsSoftwareOpen, setAppsSoftwareOpen] = useState(false);
  const [hostingDomainsOpen, setHostingDomainsOpen] = useState(false);
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    let ticking = false;
    let isScrolled = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const nextScrolled = window.scrollY > 20;
          if (nextScrolled !== isScrolled) {
            isScrolled = nextScrolled;
            setScrolled(nextScrolled);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (view: string, targetId?: string) => {
    setMobileMenuOpen(false);
    setAppsSoftwareOpen(false);
    setHostingDomainsOpen(false);
    setMoreDropdownOpen(false);
    onNavigate(view, targetId);
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-white/95 backdrop-blur-md transition-all duration-200">
      
      {/* Top Utility Bar with Contacts and Direct Paybill */}
      <div className="border-b border-slate-900/10 bg-[#080d11] text-xs text-slate-300">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1.5 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 sm:gap-6">
            <a
              href={`tel:${SITE_CONTACT.phone}`}
              onClick={() => trackCtaClick('call_click', 'Header Phone Dial', { phone: SITE_CONTACT.phoneFormatted })}
              className="flex items-center gap-1.5 hover:text-white transition-colors"
            >
              <Phone className="h-3 w-3 text-[#009fe3]" />
              <span className="font-mono">{SITE_CONTACT.phoneFormatted}</span>
            </a>
            
            <a
              href={`mailto:${SITE_CONTACT.email}`}
              onClick={() => trackCtaClick('email_click', 'Header Email Contact')}
              className="hidden items-center gap-1.5 hover:text-white transition-colors sm:flex font-mono"
            >
              <Mail className="h-3 w-3 text-[#009fe3]" />
              <span>{SITE_CONTACT.email}</span>
            </a>

            {/* Direct Paybill Quick Click in top utility bar */}
            <button
              onClick={() => {
                trackCtaClick('paybill_open', 'Header Paybill Badge');
                onOpenPaybill?.();
              }}
              className="hidden md:flex items-center gap-1.5 text-[#b88e4c] hover:text-[#d4aa63] transition-colors font-mono cursor-pointer"
              title="Click to view Paybill instructions & submit payment code"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#b88e4c]" />
              <span>Paybill: <strong>542542</strong> · Acc: <strong>01550</strong> (I&amp;M)</span>
            </button>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden xl:flex items-center gap-1.5 text-slate-600 font-mono text-xs">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span>Nairobi · Nakuru · Thika · Meru · Mombasa · Kiambu · Ruiru</span>
            </div>
            
            <a
              href={SITE_CONTACT.whatsappUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackCtaClick('whatsapp_click', 'Header WhatsApp Direct')}
              className="flex items-center gap-1 text-[#009fe3] hover:text-[#38bdf8] transition-colors font-semibold"
            >
              <MessageSquare className="h-3 w-3" />
              <span>WhatsApp Direct</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar - Fixed height to prevent layout shift & forced reflow */}
      <div className={`transition-shadow duration-200 py-2.5 ${scrolled ? 'border-b border-slate-200 shadow-xs' : 'border-b border-slate-100'}`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          
          {/* Logo */}
          <button
            onClick={() => handleNavClick('home')}
            className="flex items-center gap-2.5 text-left focus:outline-hidden group cursor-pointer shrink-0 mr-4"
          >
            <img
              src="/thinkers-arcade-logo.webp"
              alt="Thinkers Arcade - Enterprise Digital Infrastructure & Software Engineering Nairobi Kenya"
              title="Thinkers Arcade - Enterprise Digital Infrastructure Nairobi Kenya"
              width="160"
              height="40"
              className="h-9 sm:h-10 w-auto object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/logo.png';
              }}
            />
          </button>

          {/* Desktop Navigation - Combined Verticals with Mega Menus */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2 flex-1 justify-center">
            
            {/* 0. Home */}
            <button
              onClick={() => handleNavClick('home')}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 ${
                currentView === 'home'
                  ? 'text-slate-950 bg-slate-100 font-extrabold shadow-2xs'
                  : 'text-slate-700 hover:text-slate-950 hover:bg-slate-50'
              }`}
            >
              <Home className={`h-3.5 w-3.5 ${currentView === 'home' ? 'text-[#b88e4c]' : 'text-slate-400'}`} />
              <span>Home</span>
            </button>

            {/* 1. Websites */}
            <button
              onClick={() => handleNavClick('website-development')}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 ${
                currentView === 'website-development'
                  ? 'text-slate-950 bg-slate-100 font-extrabold shadow-2xs'
                  : 'text-slate-700 hover:text-slate-950 hover:bg-slate-50'
              }`}
            >
              <Globe className={`h-3.5 w-3.5 ${currentView === 'website-development' ? 'text-[#b88e4c]' : 'text-slate-400'}`} />
              <span>Websites</span>
            </button>

            {/* 2. Combined: Apps & Custom Software (Mega Menu) */}
            <div
              className="relative"
              onMouseEnter={() => setAppsSoftwareOpen(true)}
              onMouseLeave={() => setAppsSoftwareOpen(false)}
            >
              <button
                onClick={() => handleNavClick('app-development')}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-lg transition-colors cursor-pointer ${
                  currentView === 'app-development' || currentView === 'custom-erps'
                    ? 'text-slate-950 bg-slate-100 font-extrabold shadow-2xs'
                    : 'text-slate-700 hover:text-slate-950 hover:bg-slate-50'
                }`}
              >
                <span>Apps &amp; Software</span>
                <ChevronDown className={`h-3 w-3 transition-transform duration-150 ${appsSoftwareOpen ? 'rotate-180' : ''}`} />
              </button>

              {appsSoftwareOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-[540px] mt-1 rounded-2xl bg-white p-4 shadow-2xl ring-1 ring-slate-900/10 border border-slate-100 grid grid-cols-2 gap-4 z-50 animate-in fade-in duration-150 text-left">
                  
                  {/* Left Column: App Development */}
                  <div
                    onClick={() => handleNavClick('app-development')}
                    className="p-3.5 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all cursor-pointer group flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-8 w-8 rounded-lg bg-blue-50 text-[#009fe3] flex items-center justify-center border border-blue-100">
                          <Smartphone className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-slate-900 group-hover:text-[#009fe3] transition-colors">
                            App Development
                          </div>
                          <div className="text-xs text-slate-600 font-mono">
                            iOS · Android · Flutter
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        Native and cross-platform apps with offline SQLite sync and direct in-app Safaricom Daraja M-Pesa STK push.
                      </p>
                    </div>

                    <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-[#009fe3]">
                      <span>Explore Mobile Apps</span>
                      <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>

                  {/* Right Column: Custom Software & ERPs */}
                  <div
                    onClick={() => handleNavClick('custom-erps')}
                    className="p-3.5 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all cursor-pointer group flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-8 w-8 rounded-lg bg-amber-50 text-[#b88e4c] flex items-center justify-center border border-amber-100">
                          <Code2 className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-slate-900 group-hover:text-[#b88e4c] transition-colors">
                            Custom Software &amp; ERPs
                          </div>
                          <div className="text-xs text-slate-600 font-mono">
                            Excel to App · eTIMS
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        Convert spreadsheets into multi-user PostgreSQL apps with role permissions, warehouse sync, and KRA eTIMS.
                      </p>
                    </div>

                    <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-[#b88e4c]">
                      <span>Explore Custom ERPs</span>
                      <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>

                  {/* Mega Menu Footer */}
                  <div className="col-span-2 pt-2 border-t border-slate-100 flex items-center justify-between text-xs font-mono text-slate-500">
                    <span>Engineered for Nairobi, Nakuru, Thika, Meru, Mombasa, Kiambu, Ruiru</span>
                    <button
                      onClick={() => handleNavClick('solutions')}
                      className="text-slate-900 font-bold hover:underline cursor-pointer"
                    >
                      All Solutions →
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 3. Combined: Hosting & Domains (Mega Menu) */}
            <div
              className="relative"
              onMouseEnter={() => setHostingDomainsOpen(true)}
              onMouseLeave={() => setHostingDomainsOpen(false)}
            >
              <button
                onClick={() => handleNavClick('hosting')}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-lg transition-colors cursor-pointer ${
                  currentView === 'hosting' || currentView === 'domains'
                    ? 'text-slate-950 bg-slate-100 font-extrabold shadow-2xs'
                    : 'text-slate-700 hover:text-slate-950 hover:bg-slate-50'
                }`}
              >
                <span>Hosting &amp; Domains</span>
                <ChevronDown className={`h-3 w-3 transition-transform duration-150 ${hostingDomainsOpen ? 'rotate-180' : ''}`} />
              </button>

              {hostingDomainsOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-[540px] mt-1 rounded-2xl bg-white p-4 shadow-2xl ring-1 ring-slate-900/10 border border-slate-100 grid grid-cols-2 gap-4 z-50 animate-in fade-in duration-150 text-left">
                  
                  {/* Left Column: NVMe Hosting */}
                  <div
                    onClick={() => handleNavClick('hosting')}
                    className="p-3.5 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all cursor-pointer group flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-8 w-8 rounded-lg bg-blue-50 text-[#009fe3] flex items-center justify-center border border-blue-100">
                          <Server className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-slate-900 group-hover:text-[#009fe3] transition-colors">
                            NVMe Cloud Hosting
                          </div>
                          <div className="text-xs text-slate-600 font-mono">
                            LiteSpeed · cPanel · KIXP
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        PCIe Gen4 NVMe servers with sub-0.8s speed, Imunify360 WAF, and direct Paybill 542542 from KSh 3,875/yr.
                      </p>
                    </div>

                    <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-[#009fe3]">
                      <span>View Hosting Plans</span>
                      <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>

                  {/* Right Column: Domains */}
                  <div
                    onClick={() => handleNavClick('domains')}
                    className="p-3.5 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all cursor-pointer group flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-8 w-8 rounded-lg bg-amber-50 text-[#b88e4c] flex items-center justify-center border border-amber-100">
                          <Globe className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-slate-900 group-hover:text-[#b88e4c] transition-colors">
                            Domain Registration
                          </div>
                          <div className="text-xs text-slate-600 font-mono">
                            .co.ke · .com · .org
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        Official KeNIC accredited registry. Instant search, free DNS management, WHOIS privacy, and automatic renewal alerts.
                      </p>
                    </div>

                    <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-[#b88e4c]">
                      <span>Search &amp; Buy Domain</span>
                      <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>

                  {/* Mega Menu Footer */}
                  <div className="col-span-2 pt-2 border-t border-slate-100 flex items-center justify-between text-xs font-mono text-slate-500">
                    <span>Pay via M-Pesa Paybill 542542 · Account 01550 (I&amp;M)</span>
                    <button
                      onClick={() => handleNavClick('pricing')}
                      className="text-slate-900 font-bold hover:underline cursor-pointer"
                    >
                      Pricing Matrix →
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 4. POS & Hardware */}
            <button
              onClick={() => handleNavClick('store')}
              className={`px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 min-h-[44px] ${
                currentView === 'store'
                  ? 'text-slate-950 bg-slate-100 font-extrabold shadow-2xs'
                  : 'text-slate-700 hover:text-slate-950 hover:bg-slate-50'
              }`}
            >
              <ShoppingBag className="h-3.5 w-3.5 text-[#b88e4c]" />
              <span>POS &amp; Hardware</span>
            </button>

            {/* 5. Pricing Catalogue */}
            <button
              onClick={() => {
                if (currentView === 'home') {
                  const el = document.getElementById('pricing');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                  else handleNavClick('pricing');
                } else {
                  handleNavClick('pricing');
                }
              }}
              className={`px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 min-h-[44px] ${
                currentView === 'pricing'
                  ? 'text-slate-950 bg-slate-100 font-extrabold shadow-2xs'
                  : 'text-slate-700 hover:text-slate-950 hover:bg-slate-50'
              }`}
            >
              <CreditCard className="h-3.5 w-3.5 text-[#b88e4c]" />
              <span>Pricing</span>
            </button>

            {/* 6. More Dropdown (SEO, AI, Cybersecurity, Case Studies, Insights) */}
            <div
              className="relative"
              onMouseEnter={() => setMoreDropdownOpen(true)}
              onMouseLeave={() => setMoreDropdownOpen(false)}
            >
              <button
                className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold uppercase tracking-wider rounded-lg transition-colors cursor-pointer ${
                  ['seo-growth', 'ai-solutions', 'cybersecurity', 'case-studies', 'insights', 'solutions'].includes(currentView)
                    ? 'text-slate-950 bg-slate-100 font-bold'
                    : 'text-slate-600 hover:text-slate-950 hover:bg-slate-50'
                }`}
              >
                <span>More</span>
                <ChevronDown className={`h-3 w-3 transition-transform duration-150 ${moreDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {moreDropdownOpen && (
                <div className="absolute top-full right-0 w-72 mt-1 rounded-2xl bg-white p-2.5 shadow-2xl ring-1 ring-slate-900/10 border border-slate-100 space-y-1 z-50 animate-in fade-in duration-150 text-left">
                  <div className="px-3 py-1 text-xs font-mono font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 pb-1 mb-1">
                    Growth &amp; Security Verticals
                  </div>

                  <button
                    onClick={() => handleNavClick('seo-growth')}
                    className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-colors flex items-center justify-between cursor-pointer"
                  >
                    <span className="flex items-center gap-2">
                      <Search className="h-3.5 w-3.5 text-[#009fe3]" />
                      <span>SEO &amp; Google Rankings</span>
                    </span>
                    <span className="text-xs text-slate-600 font-mono">Local</span>
                  </button>

                  <button
                    onClick={() => handleNavClick('ai-solutions')}
                    className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-colors flex items-center justify-between cursor-pointer"
                  >
                    <span className="flex items-center gap-2">
                      <Bot className="h-3.5 w-3.5 text-[#b88e4c]" />
                      <span>AI &amp; Automation</span>
                    </span>
                    <span className="text-xs text-slate-600 font-mono">Bots</span>
                  </button>

                  <button
                    onClick={() => handleNavClick('cybersecurity')}
                    className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-colors flex items-center justify-between cursor-pointer"
                  >
                    <span className="flex items-center gap-2">
                      <Lock className="h-3.5 w-3.5 text-[#009fe3]" />
                      <span>Cybersecurity &amp; ODPC</span>
                    </span>
                    <span className="text-xs text-slate-600 font-mono">Audit</span>
                  </button>

                  <div className="pt-1.5 border-t border-slate-100">
                    <button
                      onClick={() => handleNavClick('solutions')}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Layers className="h-3.5 w-3.5 text-[#009fe3]" />
                      <span>All Solutions Directory</span>
                    </button>

                    <button
                      onClick={() => handleNavClick('industries')}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Building2 className="h-3.5 w-3.5 text-[#b88e4c]" />
                      <span>10 Industry Verticals</span>
                    </button>

                    <button
                      onClick={() => handleNavClick('venture-lab')}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Sparkles className="h-3.5 w-3.5 text-purple-600" />
                      <span>Venture Lab (Startup Studio)</span>
                    </button>

                    <button
                      onClick={() => handleNavClick('case-studies')}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Cpu className="h-3.5 w-3.5 text-slate-600" />
                      <span>Case Studies &amp; Portfolio</span>
                    </button>

                    <button
                      onClick={() => handleNavClick('insights')}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <TrendingUp className="h-3.5 w-3.5 text-slate-600" />
                      <span>Insights &amp; Playbooks</span>
                    </button>

                    <button
                      onClick={() => {
                        const el = document.getElementById('partners');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                        else handleNavClick('home');
                      }}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                      <span>Partners &amp; Alliances</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* Right Conversion Actions: Clean (NO paybill button here) */}
          <div className="hidden lg:flex items-center gap-2.5 shrink-0 ml-4">
            
            {/* Gold Client Portal Button */}
            <a
              href={SITE_CONTACT.portalUrl}
              target="_blank"
              rel="noreferrer"
              className="bg-[#b88e4c] hover:bg-[#a37a36] text-white font-bold px-3.5 py-2 rounded-lg text-xs flex items-center gap-1.5 transition-colors shadow-xs cursor-pointer whitespace-nowrap"
              title="Official Billing Portal: hosting.thinkersarcade.co.ke"
            >
              <span>→ Portal</span>
            </a>

            {/* High-Converting Primary CTA Button */}
            <button
              onClick={onOpenBooking}
              className="px-4 py-2 text-xs font-bold text-white bg-[#080d11] hover:bg-slate-800 rounded-lg transition-all shadow-xs flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
            >
              <span>Get Free Quote</span>
              <ArrowRight className="h-3 w-3 text-[#b88e4c]" />
            </button>
          </div>

          {/* Mobile Right Bar */}
          <div className="flex items-center gap-2 lg:hidden">
            <a
              href={SITE_CONTACT.portalUrl}
              target="_blank"
              rel="noreferrer"
              className="bg-[#b88e4c] hover:bg-[#a37a36] text-white font-bold px-3 py-2 rounded-lg text-xs flex items-center gap-1 min-h-[44px]"
            >
              <span>→ Portal</span>
            </a>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-700 hover:text-slate-900 rounded-lg hover:bg-slate-100 focus:outline-hidden cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Toggle navigation"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6 text-slate-900" />}
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Drawer Menu - Clean, Organized, Easy to Convert */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-b border-slate-200 bg-white px-4 py-5 shadow-2xl animate-in slide-in-from-top-2 duration-150 space-y-4 max-h-[85vh] overflow-y-auto text-left">
          
          {/* Main Navigation Quick Links */}
          <div>
            <div className="text-xs font-mono font-bold uppercase tracking-wider text-slate-700 mb-2">
              NAVIGATION &amp; PACKAGES
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <button
                onClick={() => handleNavClick('home')}
                className="text-left p-3 rounded-xl bg-slate-50 text-slate-900 hover:bg-slate-100 flex flex-col gap-1 border border-slate-200/80 cursor-pointer min-h-[48px]"
              >
                <Home className="h-4 w-4 text-[#855b14]" />
                <span className="text-xs font-bold">Home</span>
              </button>

              <button
                onClick={() => handleNavClick('website-development')}
                className="text-left p-3 rounded-xl bg-slate-50 text-slate-900 hover:bg-slate-100 flex flex-col gap-1 border border-slate-200/80 cursor-pointer min-h-[48px]"
              >
                <Globe className="h-4 w-4 text-[#855b14]" />
                <span className="text-xs font-bold">Websites</span>
              </button>

              <button
                onClick={() => handleNavClick('pricing')}
                className="text-left p-3 rounded-xl bg-amber-50 text-slate-950 hover:bg-amber-100 flex flex-col gap-1 border border-amber-300 cursor-pointer min-h-[48px]"
              >
                <CreditCard className="h-4 w-4 text-[#855b14]" />
                <span className="text-xs font-bold">Pricing</span>
                <span className="text-[9px] font-mono text-[#855b14] font-extrabold">From 9K</span>
              </button>
            </div>
          </div>

          {/* Main Services Section */}
          <div>
            <div className="text-xs font-mono font-bold uppercase tracking-wider text-slate-700 mb-2">
              ENGINEERING VERTICALS
            </div>
            <div className="grid grid-cols-1 gap-2">
              <button
                onClick={() => handleNavClick('app-development')}
                className="text-left p-3 rounded-xl bg-slate-50 text-slate-900 hover:bg-slate-100 flex items-center justify-between border border-slate-200/80 cursor-pointer min-h-[48px]"
              >
                <span className="flex items-center gap-2.5 text-xs font-bold">
                  <Smartphone className="h-4 w-4 text-[#009fe3]" />
                  <span>App Development</span>
                </span>
                <span className="text-xs font-mono text-[#0284c7]">iOS / Android / Flutter</span>
              </button>

              <button
                onClick={() => handleNavClick('custom-erps')}
                className="text-left p-3 rounded-xl bg-slate-50 text-slate-900 hover:bg-slate-100 flex items-center justify-between border border-slate-200/80 cursor-pointer min-h-[48px]"
              >
                <span className="flex items-center gap-2.5 text-xs font-bold">
                  <Code2 className="h-4 w-4 text-[#855b14]" />
                  <span>Custom Software &amp; ERPs</span>
                </span>
                <span className="text-xs font-mono text-[#855b14]">Excel Modernization</span>
              </button>
            </div>
          </div>

          {/* Infrastructure & Hardware */}
          <div>
            <div className="text-xs font-mono font-bold uppercase tracking-wider text-slate-700 mb-2">
              HOSTING, DOMAINS &amp; HARDWARE
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs font-semibold">
              <button
                onClick={() => handleNavClick('hosting')}
                className="text-left p-2.5 rounded-xl bg-slate-50 text-slate-900 hover:bg-slate-100 flex flex-col gap-1 border border-slate-200/80 cursor-pointer min-h-[48px]"
              >
                <Server className="h-4 w-4 text-[#0284c7]" />
                <span className="font-bold">NVMe Hosting</span>
                <span className="text-xs text-slate-600 font-mono">cPanel</span>
              </button>

              <button
                onClick={() => handleNavClick('domains')}
                className="text-left p-2.5 rounded-xl bg-slate-50 text-slate-900 hover:bg-slate-100 flex flex-col gap-1 border border-slate-200/80 cursor-pointer min-h-[48px]"
              >
                <Globe className="h-4 w-4 text-[#855b14]" />
                <span className="font-bold">Domains</span>
                <span className="text-xs text-[#855b14] font-mono">.co.ke</span>
              </button>

              <button
                onClick={() => handleNavClick('store')}
                className="text-left p-2.5 rounded-xl bg-slate-50 text-slate-900 hover:bg-slate-100 flex flex-col gap-1 border border-slate-200/80 cursor-pointer min-h-[48px]"
              >
                <ShoppingBag className="h-4 w-4 text-[#855b14]" />
                <span className="font-bold">POS &amp; Hardware</span>
                <span className="text-xs text-slate-600 font-mono">Turnkey POS</span>
              </button>
            </div>
          </div>

          {/* More Solutions */}
          <div>
            <div className="text-xs font-mono font-bold uppercase tracking-wider text-slate-700 mb-2">
              GROWTH &amp; CASE STUDIES
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <button
                onClick={() => handleNavClick('seo-growth')}
                className="text-left p-2.5 rounded-lg bg-slate-50 text-slate-900 hover:bg-slate-100 flex items-center gap-1.5 cursor-pointer font-medium min-h-[44px]"
              >
                <Search className="h-3.5 w-3.5 text-[#0284c7]" />
                <span>SEO Rankings</span>
              </button>

              <button
                onClick={() => handleNavClick('ai-solutions')}
                className="text-left p-2.5 rounded-lg bg-slate-50 text-slate-900 hover:bg-slate-100 flex items-center gap-1.5 cursor-pointer font-medium min-h-[44px]"
              >
                <Bot className="h-3.5 w-3.5 text-[#855b14]" />
                <span>AI &amp; Automation</span>
              </button>

              <button
                onClick={() => handleNavClick('cybersecurity')}
                className="text-left p-2.5 rounded-lg bg-slate-50 text-slate-900 hover:bg-slate-100 flex items-center gap-1.5 cursor-pointer font-medium min-h-[44px]"
              >
                <Lock className="h-3.5 w-3.5 text-[#0284c7]" />
                <span>Cybersecurity</span>
              </button>

              <button
                onClick={() => handleNavClick('case-studies')}
                className="text-left p-2.5 rounded-lg bg-slate-50 text-slate-900 hover:bg-slate-100 flex items-center gap-1.5 cursor-pointer font-medium min-h-[44px]"
              >
                <Cpu className="h-3.5 w-3.5 text-slate-600" />
                <span>Case Studies</span>
              </button>

              <button
                onClick={() => handleNavClick('solutions')}
                className="text-left p-2.5 rounded-lg bg-slate-50 text-slate-900 hover:bg-slate-100 flex items-center gap-1.5 cursor-pointer font-medium min-h-[44px]"
              >
                <Layers className="h-3.5 w-3.5 text-[#009fe3]" />
                <span>All Solutions</span>
              </button>

              <button
                onClick={() => handleNavClick('industries')}
                className="text-left p-2.5 rounded-lg bg-slate-50 text-slate-900 hover:bg-slate-100 flex items-center gap-1.5 cursor-pointer font-medium min-h-[44px]"
              >
                <Building2 className="h-3.5 w-3.5 text-[#b88e4c]" />
                <span>10 Industries</span>
              </button>

              <button
                onClick={() => handleNavClick('venture-lab')}
                className="text-left p-2.5 rounded-lg bg-slate-50 text-slate-900 hover:bg-slate-100 flex items-center gap-1.5 cursor-pointer font-medium min-h-[44px]"
              >
                <Sparkles className="h-3.5 w-3.5 text-purple-600" />
                <span>Venture Lab</span>
              </button>

              <button
                onClick={() => handleNavClick('insights')}
                className="text-left p-2.5 rounded-lg bg-slate-50 text-slate-900 hover:bg-slate-100 flex items-center gap-1.5 cursor-pointer font-medium min-h-[44px]"
              >
                <TrendingUp className="h-3.5 w-3.5 text-slate-600" />
                <span>Insights Hub</span>
              </button>
            </div>
          </div>

          {/* High-Converting Action CTAs */}
          <div className="pt-2 border-t border-slate-100 flex flex-col gap-2">
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                onOpenBooking();
              }}
              className="w-full py-3 px-4 rounded-xl bg-[#080d11] hover:bg-slate-800 text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer shadow-xs"
            >
              <span>Get Free Quote / Strategy Session</span>
              <ArrowRight className="h-3.5 w-3.5 text-[#b88e4c]" />
            </button>

            <a
              href={SITE_CONTACT.portalUrl}
              target="_blank"
              rel="noreferrer"
              className="w-full py-2.5 px-4 rounded-xl bg-[#b88e4c] hover:bg-[#a37a36] text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>→ Client Portal (hosting.thinkersarcade.co.ke)</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
